import { Alert } from "antd";
import PropTypes from "prop-types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./Styles.css";

import AllPatientsFiltersCard from "./AllPatientsFiltersCard";
import AllPatientsInfoCard from "./AllPatientsInfoCard";
import AllPatientsSuiviEvaluationsCard from "./AllPatientsSuiviEvaluationsCard";
import AllPatientsProgramDashboard from "./AllPatientsProgramDashboard";

export default function AllPatientsStats({
  patients = [],
  programType,
  setProgramType,
  evalsData = null,
  isLoadingEvals = false,
  sessions = [],
  isLoadingSessions = false,
  showFilters = true,
  embedded = false,
}) {
  const { t } = useTranslation("Patients");

  // Sécurise les données patients pour éviter les erreurs d'affichage
  const safePatients = useMemo(
    () => (Array.isArray(patients) ? patients : []),
    [patients]
  );

  const evalCardData = useMemo(() => {
    // Transforme la réponse backend en format attendu par la carte "Suivi évaluations"
    if (!evalsData || typeof evalsData !== "object") return null;

    const nParticipants = safePatients.length;
    const parsedCount = Number(evalsData?.count);
    const nT0 = Number.isFinite(parsedCount) ? parsedCount : nParticipants;
    const parsedCountT1 = Number(evalsData?.countT1);
    const nT1Global = Number.isFinite(parsedCountT1) ? parsedCountT1 : null;
    const tests = evalsData?.tests || {};

    const entry = (metric) => {
      const isObj = metric && typeof metric === "object";
      const t0Mean = isObj ? metric.t0Mean ?? null : metric ?? null;
      const t1Mean = isObj ? metric.t1Mean ?? null : null;
      const deltaMean = isObj ? metric.deltaMean ?? null : null;
      const nT0Metric = isObj ? metric.nT0 ?? nT0 : nT0;
      const nT1Metric = isObj ? metric.nT1 ?? nT1Global : nT1Global;

      return {
        nParticipants,
        nT0: nT0Metric,
        nT1: nT1Metric,
        t0Mean,
        t1Mean,
        deltaMean,
        riskT0: isObj ? metric.riskT0 ?? null : null,
        riskT1: isObj ? metric.riskT1 ?? null : null,
      };
    };

    return {
      sppb: entry(tests.sppb),
      sts30: entry(tests.chair),
      sts5x: entry(tests.sts5x),
      equil: entry(tests.equil),
      walk4min: entry(tests.vitesse),
      frt: entry(tests.frt),
    };
  }, [evalsData, safePatients]);

  const allSessions = useMemo(
    // Liste de sessions utilisée par le calendrier + graphiques
    () => (Array.isArray(sessions) ? sessions : []),
    [sessions]
  );

  const content = (
    <>
      {showFilters && (
        <div className="side-by-side-container">
          <div className="side-by-side-card" style={{ width: "100%" }}>
            <AllPatientsFiltersCard
              programType={programType}
              onProgramTypeChange={setProgramType}
              t={t}
            />
          </div>
        </div>
      )}

      {programType !== "PACE" && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message={t("all_stats_only_pace_data", {
            programType,
            defaultValue: "{{programType}}: aucune donnée (seul PACE est alimenté).",
          })}
        />
      )}

      {programType === "PACE" && (
        <>

      <div className="side-by-side-container">
        <div className="side-by-side-card" style={{ width: "100%" }}>
          <h2 className="section-header">{t("all_stats_information_title", "Informations")}</h2>
          <AllPatientsInfoCard
            patients={safePatients}
            t={t}
            nT0={Number.isFinite(Number(evalsData?.count)) ? Number(evalsData?.count) : null}
            nT1={Number.isFinite(Number(evalsData?.countT1)) ? Number(evalsData?.countT1) : null}
          />
        </div>
      </div>

      <div className="side-by-side-container">
        <div className="side-by-side-card all-patients-paired-card">
          <AllPatientsSuiviEvaluationsCard
            data={evalCardData}
            isLoading={isLoadingEvals}
            sessions={allSessions}
            isLoadingProgram={isLoadingSessions}
            totalParticipants={safePatients.length}
            totalNT0={
              Number.isFinite(Number(evalsData?.count)) ? Number(evalsData?.count) : null
            }
            totalNT1={
              Number.isFinite(Number(evalsData?.countT1)) ? Number(evalsData?.countT1) : null
            }
          />
        </div>
        <div className="side-by-side-card all-patients-paired-card">
          <AllPatientsProgramDashboard
            sessions={allSessions}
            isLoadingSessions={isLoadingSessions}
          />
        </div>
      </div>
        </>
      )}
    </>
  );

  if (embedded) {
    return <>{content}</>;
  }

  return <div className="patient-data-container">{content}</div>;
}

AllPatientsStats.propTypes = {
  patients: PropTypes.array,
  programType: PropTypes.oneOf(["PACE", "MATCH", "PATH"]).isRequired,
  setProgramType: PropTypes.func.isRequired,
  evalsData: PropTypes.object,
  isLoadingEvals: PropTypes.bool,
  sessions: PropTypes.array,
  isLoadingSessions: PropTypes.bool,
  showFilters: PropTypes.bool,
  embedded: PropTypes.bool,
};




